import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '../../../components/seo/SEOHead';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Spinner } from '../../../components/ui/Card';
import { api } from '../../../lib/api';
import type { User, Order } from '@bharat-consultancy/shared';

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<User | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    api.admin.customers
      .get(id)
      .then((res) => {
        setCustomer(res.customer);
        setOrders(res.orders);
      })
      .catch(() => setCustomer(null))
      .finally(() => setIsLoading(false));
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-seal-blue" />
      </div>
    );
  }

  if (!customer) return <p className="text-ink/60">Customer not found.</p>;

  return (
    <>
      <SEOHead title={`${customer.name} — Admin`} description="" canonicalPath={`/admin/customers/${customer.id}`} />

      <h1 className="font-display text-2xl font-bold text-ink">{customer.name}</h1>
      <p className="mt-1 text-ink/60">{customer.email} · {customer.phone ?? 'No phone on file'}</p>

      <div className="mt-6">
        <h2 className="font-display text-base font-semibold text-ink">Order History</h2>
        <div className="mt-3 space-y-2">
          {orders.length === 0 ? (
            <p className="text-sm text-ink/60">No orders yet.</p>
          ) : (
            orders.map((order) => (
              <Link
                key={order.id}
                to={`/admin/orders/${order.id}`}
                className="flex items-center justify-between rounded border border-line bg-white p-3 hover:shadow-stamp"
              >
                <div>
                  <p className="font-mono text-xs text-ink/50">{order.orderNumber}</p>
                  <p className="text-sm text-ink">{order.service?.title ?? 'Service'}</p>
                </div>
                <StatusBadge status={order.status} />
              </Link>
            ))
          )}
        </div>
      </div>
    </>
  );
}
